/*
 * MO visitor identity bootstrap.
 *
 * Behavioral standard events are owned by the Shopify Custom Pixel. This theme
 * asset owns only an opaque first-party visitor key, low-cardinality context,
 * Shopify Customer Privacy checks, and a bounded silent Customer Account probe.
 * It never reads or writes a shopper's email, phone, address, cart contact, or
 * Customer Account token.
 */
(function () {
  'use strict';

  const config = window.moVisitorConfig || {};
  const VISITOR_KEY = 'moVisitorId';
  const CONTEXT_KEY = 'moVisitorContext';
  const AUTH_TRIED_KEY = 'moVisitorIdentityTried';
  const AUTH_DONE_KEY = 'moVisitorIdentityDone';
  const BACKEND_ORIGIN = 'https://mo-shop-chat-agent-production.up.railway.app';
  const SILENT_ENDPOINT = config.silentEndpoint || `${BACKEND_ORIGIN}/visitor-auth/silent`;
  const VISITOR_RE = /^v-[A-Za-z0-9_-]{16,128}$/;
  let visitorInitialized = false;

  function text(value, cap) {
    if (typeof value !== 'string') return null;
    const clean = value.replace(/[\u0000-\u001f\u007f\u0085\u200b\u202e]/g, '').trim().slice(0, cap);
    return clean || null;
  }

  function token(value, cap) {
    const clean = text(value, cap);
    return clean && /^[A-Za-z0-9_-]+$/.test(clean) ? clean : null;
  }

  function makeVisitorId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return `v-${window.crypto.randomUUID()}`;
      }
      const bytes = new Uint8Array(20);
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        window.crypto.getRandomValues(bytes);
        return `v-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
      }
    } catch (error) { /* non-fatal: use a last-resort opaque random value */ }
    return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 22)}`;
  }

  function visitorId() {
    try {
      const existing = localStorage.getItem(VISITOR_KEY);
      if (existing && VISITOR_RE.test(existing)) return existing;
      const created = makeVisitorId();
      if (!VISITOR_RE.test(created)) return null;
      localStorage.setItem(VISITOR_KEY, created);
      return created;
    } catch (error) {
      return null;
    }
  }

  function storeSafeContext() {
    const context = {
      market: token(config.market, 32),
      country: token(config.country, 2),
      language: text(config.language, 16),
      version: text(config.version, 32),
    };
    try { localStorage.setItem(CONTEXT_KEY, JSON.stringify(context)); } catch (error) { /* non-fatal */ }
  }

  function consentState(api) {
    try {
      return {
        analytics: !!api.analyticsProcessingAllowed(),
        marketing: !!api.marketingAllowed(),
        saleOfData: typeof api.saleOfDataAllowed === 'function' ? !!api.saleOfDataAllowed() : false,
      };
    } catch (error) {
      return { analytics: false, marketing: false, saleOfData: false };
    }
  }

  function runSilentIdentity(id, consent) {
    if (!consent.marketing || !id) return;
    try {
      if (sessionStorage.getItem(AUTH_DONE_KEY) || sessionStorage.getItem(AUTH_TRIED_KEY)) return;
      sessionStorage.setItem(AUTH_TRIED_KEY, '1');
    } catch (error) { /* in-memory guard below still limits this page */ }

    const src = `${SILENT_ENDPOINT}?visitor_id=${encodeURIComponent(id)}&shop=${encodeURIComponent(window.location.origin)}`;
    const frame = document.createElement('iframe');
    frame.src = src;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.setAttribute('title', '');
    frame.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';
    let settled = false;

    const cleanup = function () {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };
    const onMessage = function (event) {
      if (event.origin !== BACKEND_ORIGIN) return;
      const data = event.data;
      if (!data || data.type !== 'mo-visitor-identity') return;
      if (data.ok) {
        try { sessionStorage.setItem(AUTH_DONE_KEY, '1'); } catch (error) { /* non-fatal */ }
      }
      cleanup();
    };

    window.addEventListener('message', onMessage);
    window.setTimeout(cleanup, 12000);
    document.body.appendChild(frame);
  }

  function begin(consent) {
    if (!consent.analytics) return;
    const id = visitorId();
    if (!id) return;
    if (!visitorInitialized) {
      visitorInitialized = true;
      storeSafeContext();
    }
    // A later consent update can allow marketing after analytics already started.
    // The session guard inside runSilentIdentity prevents a retry storm.
    runSilentIdentity(id, consent);
  }

  function beginWithPrivacyApi() {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) {
      // Shopify documents that tracking can proceed when its privacy API is not
      // present. This fallback still has no behavioral-event sender; the Custom
      // Pixel remains subject to its own Admin privacy configuration.
      begin({ analytics: true, marketing: true, saleOfData: false });
      return;
    }
    begin(consentState(api));
    document.addEventListener('visitorConsentCollected', function () {
      begin(consentState(api));
    });
    document.addEventListener('trackingConsentAccepted', function () {
      begin(consentState(api));
    });
  }

  function initialize() {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      beginWithPrivacyApi();
      return;
    }
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) beginWithPrivacyApi();
      });
      return;
    }
    beginWithPrivacyApi();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
}());
